import { WIDGET_PLACES, WIDGET_TYPES } from '@/components/checkout/checkout-delivery-widget/constants';
import { PICKUP_FILTERS } from '@/components/checkout/delivery-selector/pickup-filters/constants';
import { GtmEvents } from '@/gtm/gtm-events';
import cleanObject from '@/utils/helpers/cleanObject';

export class CartDeliveryPickupGtmEvents extends GtmEvents {
  constructor() {
    super('СобытияВКорзине');
  }

  addedNewFormedGroup({ groups = [], paymentMethods = [] }) {
    const paymentNames = {
      card_online: 'Онлайн',
      FPS: 'Онлайн',
      cash_or_card: 'ПриПолучении',
      cash_only: 'ПриПолучении',
    };

    const parsedGroups = groups.map((g) => ({
      id: g?.type,
      'Дата Доставки': g?.receivedOn,
      'Нужна предоплата': g?.needPrepay ? 'Да' : 'Нет',
    }));

    const filteredPaymentMethods = new Set();
    paymentMethods?.map((m) => filteredPaymentMethods.add(paymentNames[m]));
    const parsedPaymentMethods = [...filteredPaymentMethods].join(',');

    const eventLabel = JSON.stringify({
      ShippingGroups: parsedGroups,
      СпособыОплатыДоступны: parsedPaymentMethods,
    });

    return this.gtmEvent({
      eventAction: 'КРЗ_МСПВыбран',
      eventLabel,
    });
  }

  onlyOneShippingGroupFormed() {
    return this.gtmEvent({
      eventAction: 'КРЗ_МСППредвыбран',
    });
  }

  formedShippingGroupUpdated({ shippingGroups }) {
    const isMulti = shippingGroups.length > 1;

    const eventAction = isMulti
      ? 'КРЗ_МСПВыбран_Мульти' : 'КРЗ_МСПВыбран_НеМульти';
    const eventLabel = isMulti
      ? shippingGroups.reduce((acc, g) => [...acc, g.type], []) : '';

    return this.gtmEvent(cleanObject({
      eventAction,
      eventLabel,
    }));
  }

  visible() {
    return this.gtmEvent({
      eventAction: 'КРЗ_МСП_Самовывоз_ПоказЭкрана',
    });
  }

  filterTurnOn(filterName) {
    return this.gtmEvent({
      eventAction: `КРЗ_МСП_Самовывоз_Фильтр_${filterName}_Вкл`,
    });
  }

  filterTurnOff(filterName) {
    return this.gtmEvent({
      eventAction: `КРЗ_МСП_Самовывоз_Фильтр_${filterName}_Выкл`,
    });
  }

  search() {
    return this.gtmEvent({
      eventAction: 'КРЗ_МСП_Самовывоз_Поиск_Взаимодействие',
    });
  }

  pickupCardVisible() {
    return this.gtmEvent({
      eventAction: 'КРЗ_МСП_Самовывоз_ПросмотрПункта',
    });
  }

  pickupUnavailable(filters = {}) {
    const eventLabel = Object.keys(filters)
      .filter((key) => !!filters[key] && !!PICKUP_FILTERS[key])
      .map((key) => PICKUP_FILTERS[key].label)
      .join(',') || undefined;

    return this.gtmEvent({
      eventAction: 'КРЗ_МСП_Самовывоз_ДоставитьНеВозможно',
      eventLabel,
    });
  }

  drivingDirections() {
    return this.gtmEvent({
      eventAction: 'КРЗ_МСП_Самовывоз_КакДобраться',
    });
  }

  widgetAllCourierVisible(isFreeShippping) {
    if (isFreeShippping) {
      return this.gtmEvent({
        eventAction: 'КРЗ_МСП_Самовывоз_Коммун_ВсеКурьерБеспл_Показ',
      });
    }
    return this.gtmEvent({
      eventAction: 'КРЗ_МСП_Самовывоз_Коммун_ВсеКурьерПлат_Показ',
    });
  }

  widgetAllCourierClick(isFreeShippping) {
    if (isFreeShippping) {
      return this.gtmEvent({
        eventAction: 'КРЗ_МСП_Самовывоз_Коммун_ВсеКурьерБеспл_Клик',
      });
    }
    return this.gtmEvent({
      eventAction: 'КРЗ_МСП_Самовывоз_Коммун_ВсеКурьерПлат_Клик',
    });
  }

  widgetVisible({ widgetType = '', place = '', params = {} }) {
    if (widgetType === WIDGET_TYPES.onlyOneStore && place === WIDGET_PLACES.checkout) {
      return this.gtmEvent({
        eventAction: 'КРЗ_Коммун_ВсеВМаг1_Показ',
      });
    }

    if (widgetType === WIDGET_TYPES.oneStore && place === WIDGET_PLACES.checkout) {
      return this.gtmEvent({
        eventAction: 'КРЗ_Коммун_ВсеВМаг_Показ',
      });
    }

    if (widgetType === WIDGET_TYPES.oneOrder && place === WIDGET_PLACES.checkout) {
      return this.gtmEvent({
        eventAction: 'КРЗ_Коммун_ВсеМожно_Показ',
      });
    }

    if (widgetType === WIDGET_TYPES.courier && place === WIDGET_PLACES.pickup) {
      if (params.isFreeShippping) {
        return this.gtmEvent({
          eventAction: 'КРЗ_МСП_Самовывоз_Коммун_ВсеКурьерБеспл_Клик',
        });
      }
      return this.gtmEvent({
        eventAction: 'КРЗ_МСП_Самовывоз_Коммун_ВсеКурьерПлат_Клик',
      });
    }

    if (widgetType === WIDGET_TYPES.oneStore && place === WIDGET_PLACES.checkout) {
      return this.gtmEvent({
        eventAction: 'КРЗ_Коммун_ВсеВМаг_Клик',
      });
    }

    if (widgetType === WIDGET_TYPES.onlyOneStore && place === WIDGET_PLACES.checkout) {
      return this.gtmEvent({
        eventAction: 'КРЗ_Коммун_ВсеВМаг1_Клик',
      });
    }

    if (widgetType === WIDGET_TYPES.oneOrder && place === WIDGET_PLACES.checkout) {
      return this.gtmEvent({
        eventAction: 'КРЗ_Коммун_ВсеМожно_Клик',
      });
    }
  }

  widgetShippingGroupFormed(widgetType) {
    let eventAction = '';

    // eslint-disable-next-line default-case
    switch (widgetType) {
      case WIDGET_TYPES.onlyOneStore: {
        eventAction = 'КРЗ_Коммун_ВсеВМаг1_Выбран';
        break;
      }
      case WIDGET_TYPES.oneStore: {
        eventAction = 'КРЗ_Коммун_ВсеВМаг_Выбран';
        break;
      }
      case WIDGET_TYPES.oneOrder: {
        eventAction = 'КРЗ_Коммун_ВсеМожно_Выбран';
        break;
      }
    }

    if (!eventAction) {
      return;
    }

    return this.gtmEvent({
      eventAction,
    });
  }

  singleWayToGetFromManyPointsInfoShown() {
    return this.gtmEvent({
      eventAction: 'КРЗ_МСП_Самовывоз_Коммун_ВсеВМаг_Показ',
    });
  }

  singleWayToGetFromManyPointsInfoClick() {
    return this.gtmEvent({
      eventAction: 'КРЗ_МСП_Самовывоз_Коммун_ВсеВМаг_Клик',
    });
  }

  singleWayToGetFromSinglePointInfoShown() {
    return this.gtmEvent({
      eventAction: 'КРЗ_МСП_Самовывоз_Коммун_ВсеВМаг1_Показ',
    });
  }

  singleWayToGetFromSinglePointsInfoClick() {
    return this.gtmEvent({
      eventAction: 'КРЗ_МСП_Самовывоз_Коммун_ВсеВМаг1_Клик',
    });
  }

  singleWayToGetFreeDeliveryInfoShown() {
    return this.gtmEvent({
      eventAction: 'КРЗ_МСП_Курьер_Коммун_ВсеКурьерБеспл_Показ',
    });
  }

  singleWayToGetPaidDeliveryInfoShown() {
    return this.gtmEvent({
      eventAction: 'КРЗ_МСП_Курьер_Коммун_ВсеКурьерПлат_Показ',
    });
  }

  singleWayToGetFreeDeliveryInfoClick() {
    return this.gtmEvent({
      eventAction: 'КРЗ_МСП_Курьер_Коммун_ВсеКурьерБеспл_Клик',
    });
  }

  singleWayToGetPaidDeliveryInfoClick() {
    return this.gtmEvent({
      eventAction: 'КРЗ_МСП_Курьер_Коммун_ВсеКурьерПлат_Клик',
    });
  }
}

export const cartDeliveryPickupGtmEvents = new CartDeliveryPickupGtmEvents();
